import React from 'react';
import { ActionIcon, Group, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import {
  GANTT_TOOLBAR_BUTTON_RADIUS,
  GANTT_TOOLBAR_CONTROL_BORDER,
  GANTT_TOOLBAR_LABEL_STYLE,
} from './ganttToolbarStyles';

export type ZoomLevel = 'day' | 'week' | 'month' | 'year';

const ZOOM_LEVELS: ZoomLevel[] = ['day', 'week', 'month', 'year'];

const ZOOM_LABELS: Record<ZoomLevel, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

export interface ZoomControlProps {
  value: ZoomLevel;
  onChange: (level: ZoomLevel) => void;
  disabled?: boolean;
}

/**
 * Compact scale stepper (− / current / +) cycling Day → Week → Month → Year.
 */
export function ZoomControl({ value, onChange, disabled }: ZoomControlProps) {
  const index = Math.max(0, ZOOM_LEVELS.indexOf(value));
  const canDecrease = index > 0;
  const canIncrease = index < ZOOM_LEVELS.length - 1;

  const step = (delta: -1 | 1) => {
    if (disabled) return;
    const next = ZOOM_LEVELS[index + delta];
    if (next) onChange(next);
  };

  return (
    <Tooltip
      label="Choose how much time each column on the chart represents."
      multiline
      w={240}
      withArrow
    >
      <Group
        className="gantt-toolbar-zoom"
        gap={0}
        wrap="nowrap"
        align="center"
        style={{
          height: 28,
          borderRadius: GANTT_TOOLBAR_BUTTON_RADIUS,
          border: GANTT_TOOLBAR_CONTROL_BORDER,
          background: 'var(--mantine-color-body)',
          overflow: 'hidden',
          fontSize: 13,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionIcon
          variant="subtle"
          size={28}
          radius={0}
          disabled={disabled || !canDecrease}
          onClick={() => step(-1)}
          aria-label="Zoom out scale"
        >
          <IconMinus size={14} stroke={2} />
        </ActionIcon>
        <UnstyledButton
          disabled={disabled}
          aria-label={`Gantt time scale: ${ZOOM_LABELS[value]}`}
          style={{
            ...GANTT_TOOLBAR_LABEL_STYLE,
            minWidth: 56,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
            cursor: 'default',
            color: 'var(--mantine-color-text)',
          }}
        >
          <Text component="span" style={GANTT_TOOLBAR_LABEL_STYLE}>
            {ZOOM_LABELS[value]}
          </Text>
        </UnstyledButton>
        <ActionIcon
          variant="subtle"
          size={28}
          radius={0}
          disabled={disabled || !canIncrease}
          onClick={() => step(1)}
          aria-label="Zoom in scale"
        >
          <IconPlus size={14} stroke={2} />
        </ActionIcon>
      </Group>
    </Tooltip>
  );
}
